import { Link } from "react-router-dom";
import { Phone, ShieldCheck, Clock, Lock, CheckCircle2, ArrowRight, CalendarCheck } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { PageSEO } from "@/components/site/PageSEO";
import { Reveal, RevealStagger, RevealItem } from "@/components/motion/Reveal";
import { stockImages } from "@/data/site/stockImages";
import { brand } from "@/data/site/brand";
import { BookingCalendar } from "@/components/site/BookingCalendar";


const path = "/free-consultation";

const trustPoints = [
  { icon: Clock, label: "Same-day response", detail: "Most calls answered live in under 30 seconds during business hours." },
  { icon: Lock, label: "Confidential review", detail: "Protected by IRS §7216. We do not share your information." },
  { icon: ShieldCheck, label: "No obligation", detail: "Honest read on your case. No high-pressure sales, ever." },
  { icon: CheckCircle2, label: "15+ years experience", detail: "Hundreds of IRS resolution cases — individuals and businesses." },
];

const includes = [
  "Review of every IRS notice, letter, or transcript you have received",
  "Plain-English explanation of where you actually stand with the IRS",
  "Identification of every deadline running against you right now",
  "A realistic strategy — Offer in Compromise, installment agreement, audit defense, payroll-tax, or penalty abatement",
  "An honest flat-fee estimate before you engage the firm",
];

const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Is the consultation really free?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. There is no charge for the initial case evaluation and no obligation to engage the firm afterward. We will give you an honest read on what you are facing and what the realistic options are.",
      },
    },
    {
      "@type": "Question",
      name: "Is what I tell you confidential?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Communications with our CPA during the consultation are protected by IRS §7216 and our professional confidentiality obligations from the very first call.",
      },
    },
    {
      "@type": "Question",
      name: "How quickly can I speak with a CPA?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Most calls during business hours are answered live in under 30 seconds. Urgent enforcement matters — active garnishments, levies, Revenue Officer contact — are prioritized.",
      },
    },
    {
      "@type": "Question",
      name: "Do I have to come to your office?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. CPAs are federally authorized to represent taxpayers in all 50 states. The entire process can be handled remotely by phone, video, and secure document exchange. In-person meetings at our Prosper, TX office are available when you prefer.",
      },
    },
  ],
};

const serviceLd = {
  "@context": "https://schema.org",
  "@type": "AccountingService",
  name: `Free IRS Consultation — ${brand.shortName}`,
  description:
    "Free, confidential consultation with a CPA. Same-day response. No obligation. CPA-led representation from the first call.",
  url: `${brand.siteUrl}${path}`,
  telephone: brand.phoneTel,
  areaServed: ["United States"],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Free initial case evaluation",
    itemListElement: [
      { "@type": "Offer", price: "0", priceCurrency: "USD", name: "Free initial consultation" },
    ],
  },
};

const FreeConsultation = () => (
  <SiteLayout>
    <PageSEO
      title="Free IRS Consultation With a CPA"
      description={`Free, confidential consultation with a credentialed CPA. Same-day response, no obligation. Call ${brand.phone}.`}
      path={path}
      image={stockImages.consultation.src}
      breadcrumbs={[
        { name: "Home", href: "/" },
        { name: "Free Consultation", href: path },
      ]}
      jsonLd={[serviceLd, faqLd]}
    />

    <section className="relative bg-gradient-navy text-white overflow-hidden">
      <HeroBackground image={stockImages.consultation} priority />
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
        <Reveal>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-4">
            Free · Confidential · No obligation
          </p>
          <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 [text-wrap:balance]">
            Talk to a CPA about your IRS problem — <span className="text-gold">today.</span>
          </h1>
          <p className="text-lg text-white/80 max-w-2xl leading-relaxed mb-8">
            Tell us what's going on. We'll give you a straight answer on what you're facing,
            what your real options are, and what it would cost to fix — before you commit to anything.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`tel:${brand.phoneTel}`}
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-7 py-4 rounded text-lg transition-transform hover:scale-[1.03]"
            >
              <Phone className="w-5 h-5" /> Call {brand.phone}
            </a>
            <Link
              to="/schedule"
              className="inline-flex items-center justify-center gap-2 bg-white text-navy font-bold px-7 py-4 rounded text-lg hover:bg-white/90"
            >
              <CalendarCheck className="w-5 h-5" /> Book a time
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-7 py-4 rounded text-lg border border-white/20"
            >
              See office details <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <p className="text-[11px] text-white/60 mt-4">
            {brand.hours} · Avg. answer under 30 seconds
          </p>
        </Reveal>
      </div>
    </section>

    <section className="py-12 border-b border-border bg-secondary/30">
      <div className="container mx-auto px-4 max-w-5xl">
        <RevealStagger className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {trustPoints.map((t) => (
            <RevealItem key={t.label} className="flex flex-col gap-2">
              <t.icon className="w-6 h-6 text-gold" />
              <p className="font-heading font-bold text-navy text-sm">{t.label}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{t.detail}</p>
            </RevealItem>
          ))}
        </RevealStagger>
      </div>
    </section>

    <section className="py-16 lg:py-24 bg-white">
      <div className="container mx-auto px-4 max-w-5xl grid lg:grid-cols-2 gap-12">
        <Reveal>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-3">
            What's included
          </p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-5 leading-tight">
            What your free consultation actually covers.
          </h2>
          <ul className="space-y-3">
            {includes.map((item) => (
              <li key={item} className="flex gap-3 text-foreground/85 leading-relaxed">
                <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 rounded-lg border border-border bg-secondary/40 p-5">
            <p className="text-sm text-muted-foreground leading-relaxed">
              <strong className="text-navy">A note on confidentiality.</strong>{" "}
              Calling or submitting a contact request does not create a client engagement.
              Your information is protected by IRS §7216 and is never shared. Prior results
              do not guarantee a similar outcome.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="rounded-lg border border-border bg-gradient-navy text-white p-6 lg:p-10 shadow-md lg:sticky lg:top-24">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-2">
              Fastest way to talk to a CPA
            </p>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold mb-5 leading-tight">
              Call now — a CPA picks up.
            </h2>
            <a
              href={`tel:${brand.phoneTel}`}
              className="flex items-center justify-center gap-3 w-full bg-gold hover:bg-gold-light text-navy-dark font-bold py-4 rounded text-xl shadow-gold"
            >
              <Phone className="w-6 h-6" /> {brand.phone}
            </a>
            <p className="text-[11px] text-white/70 text-center mt-3">
              {brand.hours} · No call center, no salespeople.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-white/85">
              <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-gold shrink-0 mt-0.5" /> Reviewed by a credentialed CPA</li>
              <li className="flex gap-2"><Lock className="w-4 h-4 text-gold shrink-0 mt-0.5" /> Confidential — protected by IRS §7216</li>
              <li className="flex gap-2"><Clock className="w-4 h-4 text-gold shrink-0 mt-0.5" /> Same-day answers when you call</li>
            </ul>
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-[11px] uppercase tracking-[0.18em] text-gold mb-2">Prefer email?</p>
              <a href={`mailto:${brand.email}`} className="text-white/90 hover:text-gold underline-offset-4 hover:underline text-sm">
                {brand.email}
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section id="book" className="py-16 lg:py-24 bg-secondary/40 border-t border-border scroll-mt-24">
      <div className="container mx-auto px-4 max-w-4xl">
        <Reveal>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-3 text-center">
            Pick a time
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-4 text-center leading-tight">
            Schedule your free consultation.
          </h2>
          <p className="text-foreground/75 text-center max-w-2xl mx-auto mb-8 leading-relaxed">
            Choose a time that works for you. You'll get a confirmation email with a calendar
            invite and a direct number to reach us if anything changes.
          </p>
          <div className="rounded-lg bg-white border border-border shadow-sm overflow-hidden">
            <BookingCalendar minHeight={780} />
          </div>
          <p className="text-xs text-muted-foreground text-center mt-4">
            Prefer to talk right now? Call{" "}
            <a href={`tel:${brand.phoneTel}`} className="font-semibold text-navy hover:text-gold underline-offset-4 hover:underline">
              {brand.phone}
            </a>
            .
          </p>
        </Reveal>
      </div>
    </section>
  </SiteLayout>

);

export default FreeConsultation;
